import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core'
import { TranslateModule } from '@ngx-translate/core'
import { HTVButtonControllerDirective } from '../../../app-button-controller.directive'
import { ChannelService } from '../../../services/channel.service'
import { Subscription } from 'rxjs'
import { LocalChannel } from '../../../interfaces/local-channel'
import { ChannelTrackData, ChannelTrackDataFlags } from '../../../interfaces/channel-track-data'
import { MatIconModule } from '@angular/material/icon'
import { LanguageService } from '../../../services/language.service'
import { MatButtonModule } from '@angular/material/button'

interface ExtendedChannelTrackData {
  data: ChannelTrackData
  nativeLanguage: string
}

/**
 * This is a new version of the audio and subtitle menu with full black background overlay
 * This looks like menu in VOD apps (Netflix, Disney, etc)
 */
@Component({
  selector: 'app-tracks-menu-v3',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatButtonModule, MatIconModule],
  templateUrl: './tracks-menu-v3.component.html',
  styleUrls: ['./tracks-menu-v3.component.scss'],
  providers: [HTVButtonControllerDirective],
})
export class TracksMenuV3Component implements OnDestroy, OnChanges {
  channelTrackSubscription?: Subscription

  @Input() channel!: LocalChannel
  @Input() visible = false
  @Output() hide = new EventEmitter()

  isSubtitleEnabled = false
  currentAudioTrack?: ExtendedChannelTrackData
  currentSubtitleTrack?: ExtendedChannelTrackData
  audioTracks: ChannelTrackData[] = []
  subtitleTracks: ChannelTrackData[] = []
  extendedAudioTracks: ExtendedChannelTrackData[] = []
  extendedSubtitleTracks: ExtendedChannelTrackData[] = []

  readonly ChannelTrackDataFlags = ChannelTrackDataFlags

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private channelService: ChannelService,
    private languageService: LanguageService
  ) {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this.reset()
    this.registerChannelTrackListener()
  }

  ngOnDestroy(): void {
    this.channelTrackSubscription?.unsubscribe()
  }

  reset(): void {
    this.channelTrackSubscription?.unsubscribe()
    this.isSubtitleEnabled = false
    this.currentAudioTrack = undefined
    this.currentSubtitleTrack = undefined
    this.audioTracks = []
    this.subtitleTracks = []
    this.extendedAudioTracks = []
    this.extendedSubtitleTracks = []
  }

  registerChannelTrackListener(): void {
    this.channelTrackSubscription = this.channel.channelData$.subscribe(channelData => {
      if (channelData) {
        let hasListChanges = false
        this.isSubtitleEnabled = channelData.isSubtitleEnabled

        if (JSON.stringify(this.currentAudioTrack?.data) != JSON.stringify(channelData.currentAudioTrack)) {
          if (channelData.currentAudioTrack) {
            this.currentAudioTrack = this.mapToExtendedChannelTrackData(channelData.currentAudioTrack)
          } else {
            this.currentAudioTrack = undefined
          }
        }

        if (JSON.stringify(this.currentSubtitleTrack) != JSON.stringify(channelData.currentSubtitleTrack)) {
          if (channelData.currentSubtitleTrack) {
            this.currentSubtitleTrack = this.mapToExtendedChannelTrackData(channelData.currentSubtitleTrack)
          } else {
            this.currentSubtitleTrack = undefined
          }
        }

        if (JSON.stringify(this.audioTracks) != JSON.stringify(channelData.audioTracks)) {
          hasListChanges = true
          this.audioTracks = channelData.audioTracks
          this.extendedAudioTracks = channelData.audioTracks.map(at => this.mapToExtendedChannelTrackData(at))
        }

        if (JSON.stringify(this.subtitleTracks) != JSON.stringify(channelData.subtitleTracks)) {
          hasListChanges = true
          this.subtitleTracks = channelData.subtitleTracks
          this.extendedSubtitleTracks = channelData.subtitleTracks.map(st => this.mapToExtendedChannelTrackData(st))
        }

        // Reload button directive because lists have changed
        if (hasListChanges && this.visible) {
          this.appButtonDirective.initSelectedButton()
        }
      } else {
        // do nothing
      }
    })
  }

  setAudioTrack(track: ChannelTrackData): void {
    this.channelService.setAudioTrack(track)
  }

  setSubtitleTrack(track: ChannelTrackData): void {
    if (this.currentSubtitleTrack?.data.index == track.index) {
      if (this.isSubtitleEnabled) {
        this.channelService.setSubtitleTrack(track, false)
      } else {
        this.channelService.setSubtitleTrack(track, true)
      }
    } else {
      this.channelService.setSubtitleTrack(track, true)
    }
  }

  mapToExtendedChannelTrackData(data: ChannelTrackData): ExtendedChannelTrackData {
    const nativeLanguage = this.languageService.getNativeLanguage(data.lang)
    return {
      data: data,
      nativeLanguage: nativeLanguage,
    }
  }

  close(): void {
    this.hide.emit()
  }
}
